import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../components/Button";
import CheckBox from "../components/CheckBox";
import LoadingScreen from "../components/LoadingScreen";
import Modal from "../components/Modal";
import QualiphymedicalServicesAgreement from "../components/QualiphymedicalServicesAgreement";
import QualiphyServiceAgreement from "../components/QualiphyServiceAgreement";
import Text from "../components/Text";
import {
  FETCH_SIGNER_CHART_DATA,
  FETCH_SIGNER_DATA,
  GET_SIGNATURE,
} from "../redux/actions/actionTypes";
import ThankYouPage from "./thank_you";
import Layout from "../components/Layout";
import { apiKey, serverBaseUrl } from "../config/envs";
import AlreadySigned from "./already_signed";
import AwaitingNextSigner from "./awaiting_next_signer";
import LinkReplaced from "./link_replaced";
// NOVA-194: CollabPhysicianSign and PdfEditor were imported here but never
// rendered (no JSX site anywhere in src). Both POST submit_collab_final_document
// with a body-supplied sign_link_id/chart_id — the write path behind NOVA-194 —
// so a loaded-but-unrendered copy is one <JSX> away from re-arming the bug the
// NOVA-185/194 backend guards just closed. Removed the dead imports; the live
// collab reviewer is CollabChart (rendered below for document_type 3).
import CollabChart from "../components/CollabChart";

const HomePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const { signSecretId, chartId } = useParams();
  const getChartId = parseInt(chartId?.split("-")?.[0]);

  const signerData = useSelector((state) => state?.signature?.signerData);
  const signature = useSelector((state) => state?.signature);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const { signedDoc, loading } = signature;

  const signerIds = useMemo(() => {
    return signerData?.data?.document_type === 1
      ? [
          "name_of_practice_1",
          "signature_1",
          "admin_name_1",
          "title_1",
          "signature_2",
          "name_of_practice_3",
          "signature_3",
          "admin_name_3",
          "title_3",
          "submit",
        ]
      : signerData?.data?.status === 0
      ? [
          "name_of_practice_0",
          "name_of_practice_1",
          "signature_1",
          "admin_name_1",
          "admin_title_1",
          "name_of_practice_2",
          "signature_2",
          "name_of_practice_3",
          "signature_3",
          "admin_name_3",
          "admin_title_3",
          "submit",
        ]
      : [
          "md_signature_1",
          "md_name_1",
          "md_title_1",
          "md_signature_2",
          "md_name_2",
          "md_title_2",
          "submit",
        ];
  }, [signerData]);

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex > 0) {
      scrollToNext();
    } else {
      // scroll to top
      window.scrollTo(0, 0);
    }
  }, [currentIndex]);

  const scrollToNext = useCallback(() => {
    const ids = signerIds;

    if (ids.length === 0) return;

    const element = document.getElementById(ids[currentIndex - 1]);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      element?.focus();
    } else {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, signerIds]);

  const handleCheckboxChange = (value) => {
    setIsChecked(value);
  };

  useEffect(() => {
    // if (!signSecretId) {
    //   return;
    // }
    dispatch({
      type: chartId ? FETCH_SIGNER_CHART_DATA : FETCH_SIGNER_DATA,
      payload: chartId
        ? { chartId: getChartId, page: page, limit: limit, collabSecret: chartId }
        : { signSecretId },
    });
  }, [signSecretId, getChartId, dispatch, page, limit, chartId]);

  // ENG-2045: a link that no longer opens may be the clinic admin's own, retired
  // when the agreement was handed to the MD under a new secret. Ask whether it
  // was: undefined = not asked yet, null = it wasn't, else { status, replaced }.
  const signerFetchFailed = Boolean(signature?.error);
  // Kept with the secret it was asked for, and read only for that secret, so a
  // new secret starts unknown on its very first render.
  const [handOffAnswer, setHandOffAnswer] = useState({ secret: undefined, answer: undefined });
  const handOff = handOffAnswer.secret === signSecretId ? handOffAnswer.answer : undefined;
  useEffect(() => {
    if (chartId || !signSecretId || !signerFetchFailed) return undefined;
    let cancelled = false;
    const answer = (value) => !cancelled && setHandOffAnswer({ secret: signSecretId, answer: value });
    axios
      .get(`${serverBaseUrl}/sign_link_handoff_status`, {
        params: { sign_secret_id: signSecretId },
      })
      .then((res) => answer(res.data?.data ?? null))
      .catch(() => answer(null));
    return () => {
      cancelled = true;
    };
  }, [chartId, signSecretId, signerFetchFailed]);

  useEffect(() => {
    if (!loading && signerData?.data) {
      // Only open modal if consent is not already given
      setIsModalOpen(!signerData?.data?.consent);
    }
  }, [signerData?.data, loading]);

  const SubmitConsent = async () => {
    try {
      const response = await axios.post(
        `${serverBaseUrl}/sign_consent`,
        {
          qualiphy_clinic_id: signerData?.data?.qualiphy_clinic_id,
          sign_link_id: signerData?.data?.signers[0]?.sign_link_id,
          qualiphy_admin_id: signerData?.data?.qualiphy_admin_id,
          qualiphy_user_id: signerData?.data?.qualiphy_user_id,
          signer_id: signerData?.data?.signers[0]?.id,
        },
        {
          headers: {
            "sign-secret-id": signSecretId,
          },
        }
      );

      if (response.data?.success) {
        dispatch({
          type: FETCH_SIGNER_DATA,
          payload: { signSecretId: signSecretId },
        });
      } else {
        console.error("Consent submission failed:", response.data.message);
      }
    } catch (error) {
      console.error("Error submitting consent:", error.message);
    }
  };

  const CosentModal = () => {
    if (loading || !signerData?.data) {
      return null; // Don't render anything until data is ready
    }
    return (
      <Modal onContinueClick={() => SubmitConsent()} isOpen={isModalOpen}>
        <div className="flex items-center sm:flex-row gap-2 sm:gap-0 flex-col">
          <Text text="Please read the" className="text-black-700 inline mr-1" />
          <div
            onClick={() =>
              navigate("/electronic_record_and_signature_disclosure")
            }
            className="text-blue-900 hover:text-blue-800 underline cursor-pointer inline"
          >
            <Text
              text="Electronic Record and Signature Disclosure."
              className="inline"
            />
          </div>
        </div>
        <div className="pt-3 flex flex-col gap-3 sm:flex-row sm:justify-between sm:w-full items-center">
          <div>
            <CheckBox
              ischecked={isChecked}
              onChange={handleCheckboxChange}
              label="I agree to use electronic records and signatures"
            />
          </div>
          <Button
            disabled={!isChecked}
            label="Continue"
            labelColor="#0a005b"
            className="w-full sm:w-auto btn btn-primary"
            onClick={SubmitConsent}
          />
        </div>
      </Modal>
    );
  };

  if (signSecretId === "thank-you") {
    return <ThankYouPage />;
  }

  if (signerData?.data?.status === 2 && signerData?.data?.document_type !== 3) {
    return <AlreadySigned />;
  }

  if (!signerData && !getChartId) {
    if (handOff?.status === 2) return <AlreadySigned />;
    // A shared pre-fix secret: the admin or the MD may be reading this, so the
    // page adds a line for the MD.
    if (handOff?.status === 1 && handOff.replaced) return <LinkReplaced />;
    if (handOff?.status === 1) return <AwaitingNextSigner />;
    // Only secret links are looked up (chart links never hand off).
    if (!chartId && signerFetchFailed && handOff === undefined) return <LoadingScreen />;
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Text text="Sign Link not found" />
      </div>
    );
  }

  if (getChartId && signerData?.data?.data?.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Text text="No PDF found for this chart" />
      </div>
    );
  }
  return (
    <>
      {!signerData?.data ? (
        <LoadingScreen />
      ) : (
        <Layout>
          <div className="relative flex flex-col items-center justify-start min-h-screen px-4 sm:px-6 lg:px-8 overflow-y-auto">
            {signerData && !Array.isArray(signerData.data?.data) && (
              <CosentModal />
            )}
            {signerData && signerData.data?.document_type == 1 && (
              <QualiphyServiceAgreement
                scrollToNext={() => {
                  if (currentIndex < signerIds.length) {
                    setCurrentIndex(currentIndex + 1);
                  } else {
                    setCurrentIndex(0);
                  }
                }}
                signedDoc={signedDoc}
                onClose={() => setOpen(false)}
                open={open}
                data={signerData}
                onModalClick={() => setOpen(!open)}
              />
            )}
            {signerData && signerData.data?.document_type == 2 && (
              <QualiphymedicalServicesAgreement
                onClose={() => setOpen(false)}
                open={open}
                data={signerData}
                scrollToNext={() => {
                  if (currentIndex < signerIds.length) {
                    setCurrentIndex(currentIndex + 1);
                  } else {
                    setCurrentIndex(0);
                  }
                }}
                onModalClick={() => setOpen(!open)}
              />
            )}{" "}
            {signerData && signerData.data?.data?.[0]?.document_type === 3 && (
              <CollabChart
                data={signerData?.data}
                onClose={() => setOpen(false)}
                open={open}
                onModalClick={() => setOpen(!open)}
                limit={limit}
                page={page}
                setLimit={setLimit}
                setPage={setPage}
              />
            )}
          </div>
        </Layout>
      )}
    </>
  );
};

export default HomePage;
